import { useId } from 'react'
import { Crosshair, RotateCcw } from 'lucide-react'
import { IconButton, Input, Select, Textarea } from '../common'
import { InspectorField } from '../patterns/InspectorField'
import styles from './TokenField.module.css'

const HEX_COLOR_CHANNELS_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i
const INTEGER_RGB_CHANNELS_PATTERN = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i

/**
 * One field-kind-aware token editor. Shows inherited baseline vs the draft
 * override, with a reset-to-inherited affordance. The inherited value is
 * read-only context — editing writes to the override draft via `onChange`.
 *
 * Props:
 *   path:            dot-path into BrandingConfig. Used for stable keys + testids.
 *   label:           operator-facing label.
 *   kind:            'color' | 'size' | 'text' | 'toggle' | 'select' | 'gradient'.
 *   value:           current override value (undefined when inheriting).
 *   inheritedValue:  resolved baseline value for this path.
 *   dirty:           true when the draft diverges from the persisted override.
 *   onChange(next):  emit a new override value (any falsy-ish value clears).
 *   onReset():       clear this override (delegates to parent).
 *   options:         for kind === 'select', array of { value, label }.
 *   inheritanceMode: 'override' for surface overrides, 'canonical' for baseline values.
 *   onLocate():      flash the preview element(s) this token styles. Only
 *                    passed for paths with a preview element mapping.
 */
export default function TokenField({
  path,
  label,
  kind = 'text',
  value,
  inheritedValue,
  dirty = false,
  onChange,
  onReset,
  options = [],
  placeholder,
  inheritanceMode = 'override',
  inspectFocus = false,
  onLocate,
}) {
  const fieldId = useId()
  const hasValue = value !== undefined && value !== null && value !== ''
  const isOverridden = inheritanceMode === 'override' && hasValue
  const resolvedDisplay = hasValue ? value : inheritedValue
  const inputValue = hasValue ? String(value) : ''
  const stateLabel = inheritanceMode === 'canonical'
    ? (hasValue ? 'baseline' : 'default')
    : (isOverridden ? 'overridden' : 'inherited')
  const locateAction = onLocate ? (
    <IconButton
      className={styles.locateButton}
      onClick={onLocate}
      label={`Locate ${label} in preview`}
      tooltip="Show in preview"
      variant="ghost"
      data-testid={`token-field-locate-${path}`}
    >
      <Crosshair size={16} />
    </IconButton>
  ) : null
  const resetAction = hasValue && onReset ? (
    <IconButton
      className={styles.resetButton}
      onClick={onReset}
      label={inheritanceMode === 'canonical' ? `Reset ${label}` : `Reset ${label} to inherited`}
      tooltip={inheritanceMode === 'canonical' ? 'Reset field' : 'Reset to inherited'}
      variant="ghost"
      data-testid={`token-field-reset-${path}`}
    >
      <RotateCcw size={16} />
    </IconButton>
  ) : null
  const actions = locateAction || resetAction ? (
    <>
      {locateAction}
      {resetAction}
    </>
  ) : null

  return (
    <InspectorField
      label={label}
      htmlFor={fieldId}
      state={dirty ? 'dirty' : (isOverridden ? 'overridden' : 'default')}
      stateLabel={stateLabel}
      actions={actions}
      className={[
        styles.field,
        isOverridden ? styles.fieldOverridden : '',
        dirty ? styles.fieldDirty : '',
        inspectFocus ? styles.fieldInspectFocus : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={`token-field-${path}`}
      data-kind={kind}
      data-overridden={isOverridden}
      data-inspect-focus={inspectFocus ? 'true' : undefined}
    >
      {renderControl({
        fieldId,
        kind,
        inputValue,
        inheritedValue,
        resolvedDisplay,
        options,
        placeholder,
        onChange,
        path,
        inheritanceMode,
      })}
    </InspectorField>
  )
}

function renderControl({
  fieldId,
  kind,
  inputValue,
  inheritedValue,
  resolvedDisplay,
  options,
  placeholder,
  onChange,
  path,
  inheritanceMode,
}) {
  const emit = (next) => onChange(next === '' || next === null || next === undefined ? undefined : next)
  const emptyStateLabel = inheritanceMode === 'canonical' ? 'default' : 'inherit'

  switch (kind) {
    case 'color': {
      // A native color input always holds some colour; with nothing set and
      // nothing inherited its '#000000' fallback is hidden rather than shown
      // as if black were the value.
      const emptyColor = !inputValue && !inheritedValue
      return (
        <div className={styles.colorRow}>
          <input
            type="color"
            className={styles.colorSwatch}
            value={normalizeColor(inputValue || inheritedValue || '#000000')}
            onChange={(e) => emit(e.target.value)}
            aria-label={`${path} color swatch`}
            data-empty={emptyColor ? 'true' : undefined}
            data-testid={`token-field-color-swatch-${path}`}
          />
          <Input
            id={fieldId}
            type="text"
            className={styles.textInput}
            size="compact"
            value={inputValue}
            placeholder={inheritedValue || emptyStateLabel}
            onChange={(e) => emit(e.target.value)}
            data-testid={`token-field-color-text-${path}`}
          />
        </div>
      )
    }

    case 'size':
      return (
        <Input
          id={fieldId}
          type="text"
          className={styles.textInput}
          size="compact"
          value={inputValue}
          placeholder={inheritedValue || placeholder || (inheritanceMode === 'canonical' ? 'default size' : 'e.g. 12px')}
          onChange={(e) => emit(e.target.value)}
          data-testid={`token-field-size-${path}`}
        />
      )

    case 'toggle':
      return (
        <label className={styles.toggleRow}>
          <input
            id={fieldId}
            type="checkbox"
            checked={resolvedDisplay === true || resolvedDisplay === 'true'}
            onChange={(e) => emit(e.target.checked)}
            data-testid={`token-field-toggle-${path}`}
          />
          <span className={styles.toggleValue}>
            {inputValue
              ? String(inputValue)
              : `(${inheritanceMode === 'canonical' ? 'default' : 'inherited'}: ${String(inheritedValue)})`}
          </span>
        </label>
      )

    case 'select':
      return (
        <Select
          id={fieldId}
          className={styles.textInput}
          size="compact"
          value={inputValue}
          onChange={(e) => emit(e.target.value)}
          data-testid={`token-field-select-${path}`}
        >
          <option value="">
            {inheritanceMode === 'canonical' ? 'default' : 'inherited'} ({inheritedValue})
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      )

    case 'gradient':
      return (
        <div className={styles.gradientRow}>
          <div
            className={styles.gradientSwatch}
            style={{ backgroundImage: resolvedDisplay || 'none' }}
            aria-hidden="true"
          />
          <Textarea
            id={fieldId}
            className={styles.textArea}
            size="compact"
            value={inputValue}
            rows={2}
            placeholder={inheritedValue || (inheritanceMode === 'canonical' ? 'default gradient' : 'linear-gradient(...)')}
            onChange={(e) => emit(e.target.value)}
            data-testid={`token-field-gradient-${path}`}
          />
        </div>
      )

    case 'text':
    default:
      return (
        <Input
          id={fieldId}
          type="text"
          className={styles.textInput}
          size="compact"
          value={inputValue}
          placeholder={inheritedValue || placeholder || emptyStateLabel}
          onChange={(e) => emit(e.target.value)}
          data-testid={`token-field-text-${path}`}
        />
      )
  }
}

function normalizeColor(raw) {
  const text = String(raw || '').trim()
  if (HEX_COLOR_CHANNELS_PATTERN.test(text)) {
    if (text.length === 4) {
      const [, r, g, b] = text
      return `#${r}${r}${g}${g}${b}${b}`
    }
    return text
  }
  const rgbMatch = text.match(INTEGER_RGB_CHANNELS_PATTERN)
  if (rgbMatch) {
    return `#${[rgbMatch[1], rgbMatch[2], rgbMatch[3]]
      .map((ch) => Number(ch).toString(16).padStart(2, '0'))
      .join('')}`
  }
  return '#000000'
}
